import { useState, FormEvent, useEffect, useRef } from "react";
import { FadeUp, ScaleIn, StaggerContainer } from "@/components/base/ScrollAnimations";
import { companyInfo, contactFormFields } from "@/mocks/websiteData";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("company_alt");
    if (honeypot && typeof honeypot === "string" && honeypot.trim() !== "") {
      setStatus("success");
      form.reset();
      setCharCount(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "company_alt") {
        payload.append(key, value.toString());
      }
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/d9ksaa0h9dsfbuoi6jg0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;

      if (
        !response.ok ||
        (parsed && parsed.code !== "OK") ||
        serverMsg.toLowerCase().includes("spam") ||
        serverMsg.toLowerCase().includes("form data is spam")
      ) {
        setStatus("error");
        setErrorMsg(serverMsg || "Something went wrong. Please try again.");
        return;
      }

      setStatus("success");
      form.reset();
      setCharCount(0);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Network error. Please try again.");
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[260px] md:h-[340px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20customer%20service%20office%20reception%20with%20friendly%20professional%20atmosphere%2C%20warm%20lighting%2C%20green%20plants%2C%20clean%20minimal%20interior%2C%20solar%20panel%20posters%20on%20wall%2C%20professional%20photography&width=1600&height=700&seq=contact-hero-01&orientation=landscape"
          alt="Contact us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/85"></div>
        <div className="relative z-10 section-padding text-center w-full">
          <div className={`transition-all duration-1000 ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/25 backdrop-blur-sm border border-accent-400/30 mb-4">
              <span className="text-sm font-medium text-accent-300">Response within 24 hours</span>
            </div>
          </div>
          <div className={`transition-all duration-1000 ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm font-medium text-background-100/80 mb-2 tracking-wide">NexSunNexova Solar Energy Pvt. Ltd.</p>
          </div>
          <div className={`transition-all duration-1000 delay-200 ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 mb-3">Contact Us</h1>
          </div>
          <div className={`transition-all duration-1000 delay-400 ${heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto">Ready to go solar? Get a free quote or book a site survey today.</p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <FadeUp>
                <div>
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-4">Get in Touch</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    Our solar experts are available to answer your questions, provide pricing, and schedule free site surveys across Telangana.
                  </p>
                </div>
              </FadeUp>

              <StaggerContainer staggerDelay={100} className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300/40 transition-all hover:-translate-y-0.5 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-900">Address</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mt-1">{companyInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300/40 transition-all hover:-translate-y-0.5 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-900">Phone</h3>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                      {companyInfo.phones.map((phone) => (
                        <a key={phone} href={`tel:${phone}`} className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                          {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300/40 transition-all hover:-translate-y-0.5 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-900">Email</h3>
                    <a href={`mailto:${companyInfo.email}`} className="text-sm text-primary-600 hover:text-primary-700 transition-colors mt-1 block">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300/40 transition-all hover:-translate-y-0.5 shadow-sm">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-time-line w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-900">Business Hours</h3>
                    <p className="text-sm text-foreground-600 mt-1">Monday – Saturday: 9:00 AM – 7:00 PM</p>
                    <p className="text-sm text-foreground-600">Sunday: 10:00 AM – 4:00 PM</p>
                  </div>
                </div>
              </StaggerContainer>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <ScaleIn>
                <div className="p-5 md:p-8 rounded-xl bg-background-100 border border-background-200/70 shadow-sm">
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-1">Request a Free Quote</h3>
                  <p className="text-sm text-foreground-600 mb-5">Fill in your details and our team will reach out within 24 hours.</p>

                  <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="true" id="quote-form" className="space-y-4">
                    <div className="honeypot-field">
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          pattern="[0-9]{10,12}"
                          className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                          placeholder="10-digit mobile number"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="service_type" className="block text-sm font-medium text-foreground-700 mb-1.5">Service Interested In *</label>
                        <select
                          id="service_type"
                          name="service_type"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                        >
                          <option value="">Select a service</option>
                          {contactFormFields.serviceTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="capacity" className="block text-sm font-medium text-foreground-700 mb-1.5">Required Capacity</label>
                        <select
                          id="capacity"
                          name="capacity"
                          className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                        >
                          <option value="">Select capacity</option>
                          {contactFormFields.capacityOptions.map((cap) => (
                            <option key={cap} value={cap}>{cap}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        onChange={handleTextareaChange}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors resize-y"
                        placeholder="Tell us about your requirements, site location, or any questions..."
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1 text-right">{charCount}/500</p>
                    </div>

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all whitespace-nowrap hover:-translate-y-0.5 shadow-md shadow-primary-500/20"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line w-5 h-5 flex items-center justify-center animate-spin"></i>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center"></i>
                          <span>Submit Quote Request</span>
                        </>
                      )}
                    </button>

                    {status === "success" && (
                      <div className="p-4 rounded-lg bg-accent-100 border border-accent-200 text-accent-800 text-sm flex items-start gap-2">
                        <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-accent-600 shrink-0"></i>
                        <div>
                          <p className="font-semibold">Thank you! Your request has been submitted.</p>
                          <p className="text-accent-700 mt-0.5">Our solar expert will contact you within 24 hours.</p>
                        </div>
                      </div>
                    )}

                    {status === "error" && (
                      <div className="p-4 rounded-lg bg-primary-100 border border-primary-200 text-primary-800 text-sm flex items-start gap-2">
                        <i className="ri-error-warning-line w-5 h-5 flex items-center justify-center text-primary-600 shrink-0"></i>
                        <div>
                          <p className="font-semibold">Submission failed.</p>
                          <p className="text-primary-700 mt-0.5">{errorMsg}</p>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </ScaleIn>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-8 md:py-12 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="max-w-6xl mx-auto">
              <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-900 mb-4 text-center">Find Us</h2>
              <div className="rounded-xl overflow-hidden border border-background-200/70 h-64 md:h-80 shadow-sm">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3794.123!2d79.5603!3d17.9916!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDU5JzI5LjgiTiA3OcKwMzMnMzcuMSJF!5e0!3m2!1sen!2sin!4v1600000000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="NexSunNexova Office Location"
                  aria-label="Google Maps showing NexSunNexova Solar Energy office in Hanmakonda"
                ></iframe>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}